import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useState } from "react";
import { Button } from "@project-mgmt/ui";
import { downloadVentureFile, deleteVentureFile } from "../api/ventureFiles";
/**
 * File list component for ventures
 * Displays uploaded files with download and delete actions
 *
 * Requirements:
 * - 5.4: File listing and download
 * - 5.5: File deletion
 */
export const VentureFileList = ({ ventureId, workspaceId, files, canDelete = false, onFileDeleted, className = "", }) => {
    const [deletingFileId, setDeletingFileId] = useState(null);
    const [downloadingFileId, setDownloadingFileId] = useState(null);
    /**
     * Handle file download
     */
    const handleDownload = useCallback(async (fileId) => {
        try {
            setDownloadingFileId(fileId);
            await downloadVentureFile(ventureId, workspaceId, fileId);
        }
        catch (error) {
            console.error("Failed to download file:", error);
            alert("Failed to download file. Please try again.");
        }
        finally {
            setDownloadingFileId(null);
        }
    }, [ventureId, workspaceId]);
    /**
     * Handle file deletion
     */
    const handleDelete = useCallback(async (fileId, filename) => {
        if (!confirm(`Are you sure you want to delete "${filename}"?`)) {
            return;
        }
        try {
            setDeletingFileId(fileId);
            await deleteVentureFile(ventureId, workspaceId, fileId);
            onFileDeleted?.(fileId);
        }
        catch (error) {
            console.error("Failed to delete file:", error);
            alert("Failed to delete file. Please try again.");
        }
        finally {
            setDeletingFileId(null);
        }
    }, [ventureId, workspaceId, onFileDeleted]);
    /**
     * Format file size
     */
    const formatFileSize = useCallback((bytes) => {
        if (!bytes)
            return "Unknown size";
        if (bytes < 1024)
            return `${bytes} B`;
        if (bytes < 1024 * 1024)
            return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    }, []);
    /**
     * Get file icon based on mime type
     */
    const getFileIcon = useCallback((mimeType) => {
        if (!mimeType)
            return "📄";
        if (mimeType.startsWith("image/"))
            return "🖼️";
        if (mimeType === "application/pdf")
            return "📕";
        return "📄";
    }, []);
    if (files.length === 0) {
        return (_jsxs("div", { className: `text-center py-8 text-gray-500 ${className}`, children: [_jsx("div", { className: "text-4xl mb-2", "aria-hidden": "true", children: "\uD83D\uDCC1" }), _jsx("p", { className: "text-sm", children: "No files uploaded yet" })] }));
    }
    return (_jsx("div", { className: `space-y-2 ${className}`, children: files.map((file) => (_jsxs("div", { className: "flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors", children: [_jsxs("div", { className: "flex items-center gap-3 flex-1 min-w-0", children: [_jsx("div", { className: "text-2xl flex-shrink-0", "aria-hidden": "true", children: getFileIcon(file.mimeType) }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("div", { className: "text-sm font-medium text-gray-900 truncate", children: file.filename }), _jsxs("div", { className: "text-xs text-gray-500", children: [formatFileSize(file.fileSize), file.uploader?.name && ` • Uploaded by ${file.uploader.name}`] })] })] }), _jsxs("div", { className: "flex items-center gap-2 flex-shrink-0", children: [_jsx(Button, { onClick: () => handleDownload(file.id), disabled: downloadingFileId === file.id, variant: "outline", size: "sm", "aria-label": `Download ${file.filename}`, children: downloadingFileId === file.id ? "⏳" : "⬇️" }), canDelete && (_jsx(Button, { onClick: () => handleDelete(file.id, file.filename), disabled: deletingFileId === file.id, variant: "outline", size: "sm", "aria-label": `Delete ${file.filename}`, children: deletingFileId === file.id ? "⏳" : "🗑️" }))] })] }, file.id))) }));
};
